import React, { useState, useEffect } from 'react';
import {
  ShieldAlert,
  RefreshCw,
  Filter,
  ShieldCheck,
  Activity,
  Users,
  Key,
  Search,
  Plus,
  Lock,
  Unlock,
  Check,
  Copy,
  Eye,
  EyeOff,
  Trash2,
  UserPlus,
  Mail,
  UserCheck,
  AlertCircle,
  CheckCircle2,
  Clock,
  Layers,
  ChevronRight,
  Shield,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { usePermissions } from '../../context/PermissionsContext.tsx';
import { RolesPermissionsView } from './RolesPermissionsView.tsx';
import { AuditLogEntry, User, UserRole, Department } from '../../../shared/types.ts';
import { Avatar } from '../common/Avatar.tsx';
import { RoleBadge } from '../common/Badge.tsx';
import { Modal } from '../common/Modal.tsx';
import { InviteModal } from '../team/InviteModal.tsx';

// Helper to generate a strong random password
const generateSecurePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%&*';
  let pass = 'Pulse-';
  for (let i = 0; i < 8; i++) {
    pass += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return pass;
};

export const AuditLogView: React.FC = () => {
  const { token, user } = useAuth();
  const { can } = usePermissions();

  // Top Section Tabs: 'users' (Users & Credentials), 'audit' (Audit Trail), 'rbac' (Roles & Permissions)
  const [activeTab, setActiveTab] = useState<'users' | 'audit' | 'rbac'>('users');

  // Audit Logs State
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  // Users & Credentials State
  const [members, setMembers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState<string>('all');
  const [userVerificationFilter, setUserVerificationFilter] = useState<string>('all');

  // Modals & Actions
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState<User | null>(null);

  // Set Password Modal
  const [setPasswordUser, setSetPasswordUser] = useState<User | null>(null);
  const [newCustomPassword, setNewCustomPassword] = useState('');
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [passwordActionSuccess, setPasswordActionSuccess] = useState<string | null>(null);
  const [passwordActionError, setPasswordActionError] = useState<string | null>(null);

  // Add User Form State
  const [newFullName, setNewFullName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState(generateSecurePassword);
  const [showPassword, setShowPassword] = useState(false);
  const [newRole, setNewRole] = useState<UserRole>('member');
  const [newDepartmentId, setNewDepartmentId] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [isSubmittingNewUser, setIsSubmittingNewUser] = useState(false);
  const [newUserError, setNewUserError] = useState<string | null>(null);

  // Generated Credentials Modal State
  const [credentialsResult, setCredentialsResult] = useState<{
    user: any;
    generatedPassword?: string;
    verificationCode?: string;
    emailDelivered?: boolean;
    message?: string;
  } | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const getAuthHeaders = (extra: Record<string, string> = {}) => ({
    Authorization: `Bearer ${token || ''}`,
    ...extra,
  });

  // Fetch Audit Logs
  const fetchLogs = async () => {
    if (!token) return;
    setIsLoadingLogs(true);
    let url = '/api/audit?limit=100';
    if (filterAction) url += `&action=${encodeURIComponent(filterAction)}`;
    if (filterEntity) url += `&entityType=${encodeURIComponent(filterEntity)}`;

    try {
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.auditLogs || []);
      }
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  // Fetch Users & Departments
  const fetchMembers = async () => {
    if (!token) return;
    setIsLoadingMembers(true);
    try {
      const [membersRes, deptsRes] = await Promise.all([
        fetch('/api/team/members', { headers: getAuthHeaders() }),
        fetch('/api/team/departments', { headers: getAuthHeaders() }),
      ]);
      if (membersRes.ok) {
        const data = await membersRes.json();
        setMembers(data.members || data.users || []);
      }
      if (deptsRes.ok) {
        const deptsData = await deptsRes.json();
        setDepartments(deptsData.departments || []);
      }
    } catch (err) {
      console.error('Failed to load team members:', err);
    } finally {
      setIsLoadingMembers(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'audit') {
      fetchLogs();
    } else if (activeTab === 'users') {
      fetchMembers();
    }
  }, [token, activeTab, filterAction, filterEntity]);

  // Initial load
  useEffect(() => {
    fetchMembers();
  }, [token]);

  // Handle Manual Direct Verification of User Account
  const handleManualVerify = async (userId: string) => {
    if (!token) return;
    try {
      const res = await fetch(`/api/team/members/${userId}/verify`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        fetchMembers();
      }
    } catch (err) {
      console.error('Failed to manually verify user:', err);
    }
  };

  // Handle Regenerating and Resending Credentials
  const handleRegenerateCredentials = async (member: User) => {
    if (!token) return;
    try {
      const res = await fetch(`/api/team/members/${member.id}/resend-credentials`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        setCredentialsResult({
          user: member,
          generatedPassword: data.generatedPassword,
          verificationCode: data.verificationCode,
          emailDelivered: data.emailDelivered,
          message: data.message,
        });
        fetchMembers();
      } else {
        alert(data.error || 'Failed to regenerate credentials.');
      }
    } catch (err: any) {
      alert(err?.message || 'Failed to regenerate credentials.');
    }
  };

  // Handle Setting Specific Password
  const handleSetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !setPasswordUser) return;
    if (!newCustomPassword || newCustomPassword.trim().length < 6) {
      setPasswordActionError('Password must be at least 6 characters long.');
      return;
    }
    setIsSubmittingPassword(true);
    setPasswordActionError(null);
    setPasswordActionSuccess(null);
    try {
      const res = await fetch(`/api/team/members/${setPasswordUser.id}/set-password`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ newPassword: newCustomPassword.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        setPasswordActionSuccess(data.message || 'Password updated successfully.');
        setTimeout(() => {
          setSetPasswordUser(null);
          setNewCustomPassword('');
          setPasswordActionSuccess(null);
          fetchMembers();
        }, 1200);
      } else {
        setPasswordActionError(data.error || 'Failed to update password.');
      }
    } catch (err: any) {
      setPasswordActionError(err?.message || 'Network error.');
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  // Handle Creating a New User
  const handleCreateUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setNewUserError(null);
    setIsSubmittingNewUser(true);
    try {
      const res = await fetch('/api/team/members', {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          fullName: newFullName.trim(),
          email: newEmail.trim().toLowerCase(),
          password: newPassword,
          role: newRole,
          departmentId: newDepartmentId || null,
          title: newTitle.trim() || null,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setIsAddUserOpen(false);
        setCredentialsResult({
          user: data.user,
          generatedPassword: data.generatedPassword,
          verificationCode: data.verificationCode,
          emailDelivered: data.emailDelivered,
          message: data.message,
        });
        setNewFullName('');
        setNewEmail('');
        setNewTitle('');
        setNewPassword(generateSecurePassword());
        fetchMembers();
      } else {
        setNewUserError(data.error || 'Failed to create user account.');
      }
    } catch (err: any) {
      setNewUserError(err?.message || 'Network error.');
    } finally {
      setIsSubmittingNewUser(false);
    }
  };

  // Handle Updating User Role
  const handleUpdateRole = async (targetUserId: string, updatedRole: UserRole) => {
    if (!token) return;
    try {
      const res = await fetch(`/api/team/members/${targetUserId}`, {
        method: 'PUT',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ role: updatedRole }),
      });
      if (res.ok) {
        setSelectedUserForRole(null);
      } else {
        const body = await res.json().catch(() => ({}));
        alert(body.error || `Could not change the role (HTTP ${res.status}).`);
      }
      // Reload either way so the dropdown shows what is really saved
      fetchMembers();
    } catch (err) {
      console.error('Failed to update user role:', err);
      alert('Could not change the role: the server could not be reached.');
      fetchMembers();
    }
  };

  // Handle Deleting / Removing User
  const handleDeleteUser = async (targetUserId: string) => {
    if (!token) return;
    setIsDeleting(true);
    try {
      const res = await fetch(`/api/team/members/${targetUserId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        setMemberToDelete(null);
        fetchMembers();
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to remove user.');
      }
    } catch (err: any) {
      alert(err?.message || 'Failed to remove user.');
    } finally {
      setIsDeleting(false);
    }
  };

  const copyToClipboard = (text: string, keyName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyName);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Filtered Members
  const filteredMembers = members.filter((m) => {
    if (userRoleFilter !== 'all' && m.role !== userRoleFilter) return false;
    if (userVerificationFilter === 'verified' && !Boolean(m.isVerified)) return false;
    if (userVerificationFilter === 'pending' && Boolean(m.isVerified)) return false;
    if (userSearch.trim()) {
      const term = userSearch.toLowerCase();
      return (
        m.fullName.toLowerCase().includes(term) ||
        m.email.toLowerCase().includes(term) ||
        (m.title && m.title.toLowerCase().includes(term)) ||
        (m.departmentName && m.departmentName.toLowerCase().includes(term))
      );
    }
    return true;
  });

  const verifiedCount = members.filter((m) => Boolean(m.isVerified)).length;
  const pendingCount = members.filter((m) => !Boolean(m.isVerified)).length;
  const adminCount = members.filter((m) => m.role === 'admin').length;

  if (!can('security.view')) {
    return (
      <div className="p-12 text-center text-xs text-rose-500 font-medium">
        Access Denied. Your role does not have access to the Security screen. An administrator can change this on the Roles & Access screen.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Top Section Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800 shadow-2xs">
        <div className="flex items-center space-x-2">
          <ShieldAlert className="w-4 h-4 text-brand-600 dark:text-brand-400 shrink-0" />
          <h2 className="text-sm sm:text-base font-extrabold text-surface-900 dark:text-surface-100 tracking-tight uppercase">
            Security, Credentials & Audit Center
          </h2>
        </div>

        {/* Global Action Buttons */}
        <div className={`flex items-center space-x-1.5 ${can('users.manage') ? '' : 'hidden'}`}>
          <button
            onClick={() => setIsInviteOpen(true)}
            className="px-2.5 py-1 rounded-xl border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 text-surface-700 dark:text-surface-200 text-[11px] font-bold flex items-center space-x-1 hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors shadow-2xs"
          >
            <Mail className="w-3.5 h-3.5 text-brand-500" />
            <span>Multi-User Invite</span>
          </button>
          <button
            onClick={() => setIsAddUserOpen(true)}
            className="px-3 py-1 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-[11px] font-bold flex items-center space-x-1 shadow-2xs transition-colors"
          >
            <UserPlus className="w-3.5 h-3.5" />
            <span>Add User & Credentials</span>
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex items-center space-x-1 p-1 bg-surface-100 dark:bg-surface-800/60 rounded-xl w-fit border border-surface-200 dark:border-surface-700/60">
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
            activeTab === 'users'
              ? 'bg-white dark:bg-surface-900 text-brand-600 dark:text-brand-400 shadow-sm'
              : 'text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-surface-100'
          }`}
        >
          <Users className="w-3.5 h-3.5" />
          <span>Users & Credentials</span>
          <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] bg-brand-50 dark:bg-brand-950/40 text-brand-700 dark:text-brand-300">
            {members.length}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('audit')}
          className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
            activeTab === 'audit'
              ? 'bg-white dark:bg-surface-900 text-brand-600 dark:text-brand-400 shadow-sm'
              : 'text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-surface-100'
          }`}
        >
          <Activity className="w-3.5 h-3.5" />
          <span>Security & Audit Trail</span>
          <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] bg-surface-200 dark:bg-surface-700 text-surface-700 dark:text-surface-300">
            {logs.length > 0 ? logs.length : 'Live'}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('rbac')}
          className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
            activeTab === 'rbac'
              ? 'bg-white dark:bg-surface-900 text-brand-600 dark:text-brand-400 shadow-sm'
              : 'text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-surface-100'
          }`}
        >
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Roles & Access</span>
        </button>
      </div>

      {/* ================= TAB 1: USERS & CREDENTIALS ================= */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          {/* Quick Metrics Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
            <div className="p-3.5 rounded-2xl bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 shadow-card">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-surface-500">Total Accounts</span>
                <Users className="w-4 h-4 text-brand-500" />
              </div>
              <div className="text-xl font-bold text-surface-900 dark:text-surface-100 mt-1">
                {members.length}
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 shadow-card">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-surface-500">Verified & Active</span>
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              </div>
              <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                {verifiedCount}
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 shadow-card">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-surface-500">Pending Verification</span>
                <Clock className="w-4 h-4 text-amber-500" />
              </div>
              <div className="text-xl font-bold text-amber-600 dark:text-amber-400 mt-1">
                {pendingCount}
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 shadow-card">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-surface-500">Administrators</span>
                <Shield className="w-4 h-4 text-purple-500" />
              </div>
              <div className="text-xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                {adminCount}
              </div>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="p-3.5 sm:p-4 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800 shadow-card flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3 flex-1 min-w-[260px]">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                <input
                  type="text"
                  placeholder="Search user by name, email, or designation..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="w-full text-xs pl-8 pr-3 py-1.5 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>

              <select
                value={userRoleFilter}
                onChange={(e) => setUserRoleFilter(e.target.value)}
                className="text-xs px-3 py-1.5 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-700 dark:text-surface-300 focus:outline-none"
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="team_lead">Team Lead</option>
                <option value="member">Member</option>
                <option value="sqa">SQA</option>
                <option value="guest">Guest</option>
                <option value="viewer">Viewer</option>
              </select>

              <select
                value={userVerificationFilter}
                onChange={(e) => setUserVerificationFilter(e.target.value)}
                className="text-xs px-3 py-1.5 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-700 dark:text-surface-300 focus:outline-none"
              >
                <option value="all">All Statuses</option>
                <option value="verified">Verified Only</option>
                <option value="pending">Pending Verification</option>
              </select>
            </div>

            <button
              onClick={fetchMembers}
              disabled={isLoadingMembers}
              className="px-3 py-1.5 rounded-xl border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 text-surface-700 dark:text-surface-200 text-xs font-semibold flex items-center space-x-1.5 hover:bg-surface-50 transition-colors shrink-0"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isLoadingMembers ? 'animate-spin' : ''}`} />
              <span>Refresh Users</span>
            </button>
          </div>

          {/* Users & Credentials Table */}
          <div className="bg-white dark:bg-surface-900 rounded-2xl shadow-card border border-surface-200 dark:border-surface-800 overflow-hidden">
            <div
              onWheel={(e) => {
                if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.deltaY === 0) return;
                const el = e.currentTarget;
                const canScrollHorizontally =
                  (e.deltaY > 0 && el.scrollLeft + el.clientWidth < el.scrollWidth - 2) ||
                  (e.deltaY < 0 && el.scrollLeft > 1);
                if (e.shiftKey && canScrollHorizontally) {
                  e.preventDefault();
                  el.scrollLeft += e.deltaY;
                }
              }}
              className="overflow-x-auto touch-scroll"
            >
              <table className="w-full min-w-[760px] text-left text-xs">
                <thead className="bg-surface-50 dark:bg-surface-800/60 border-b border-surface-200 dark:border-surface-800 uppercase tracking-wider text-[11px] font-semibold text-surface-500">
                  <tr>
                    <th className="py-3 px-4">User & Email</th>
                    <th className="py-3 px-4">Role</th>
                    <th className="py-3 px-4">Department / Space</th>
                    <th className="py-3 px-4">Credentials & Verification</th>
                    <th className="py-3 px-4 text-right">Security Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-100 dark:divide-surface-800/60">
                  {filteredMembers.map((member) => {
                    const isVerified = Boolean(member.isVerified);
                    return (
                      <tr key={member.id} className="hover:bg-surface-50 dark:hover:bg-surface-800/40 transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-3">
                            <Avatar src={member.avatarUrl} name={member.fullName} size="sm" />
                            <div>
                              <div className="font-bold text-surface-900 dark:text-surface-100 flex items-center space-x-1.5">
                                <span>{member.fullName}</span>
                                {member.id === user?.id && (
                                  <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-surface-100 dark:bg-surface-800 text-surface-500">
                                    You
                                  </span>
                                )}
                              </div>
                              <div className="text-surface-500 font-mono text-[11px] select-all">
                                {member.email}
                              </div>
                              {member.title && (
                                <div className="text-[10px] text-surface-400 mt-0.5">
                                  {member.title}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="py-3 px-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <RoleBadge role={member.role} />
                            {/* Role changer dropdown */}
                            <select
                              value={member.role}
                              onChange={(e) => handleUpdateRole(member.id, e.target.value as UserRole)}
                              aria-label="Change user role"
                              disabled={!can('users.manage') || (member.role === 'admin' && user?.role !== 'admin')}
                              title={can('users.manage') ? undefined : 'Your role cannot change user roles'}
                              className="text-[10px] font-semibold bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-lg px-2 py-0.5 text-surface-700 dark:text-surface-300 cursor-pointer focus:ring-1 focus:ring-brand-500 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              <option value="admin">Admin</option>
                              <option value="team_lead">Team Lead</option>
                              <option value="member">Member</option>
                              <option value="sqa">SQA</option>
                              <option value="guest">Guest</option>
                              <option value="viewer">Viewer</option>
                            </select>
                          </div>
                        </td>

                        <td className="py-3 px-4 text-surface-600 dark:text-surface-300 whitespace-nowrap">
                          {member.departmentName ? (
                            <span className="px-2 py-0.5 rounded-lg bg-surface-100 dark:bg-surface-800 text-[11px] font-medium border border-surface-200 dark:border-surface-700">
                              {member.departmentName}
                            </span>
                          ) : (
                            <span className="text-surface-400 text-[11px]">—</span>
                          )}
                        </td>

                        <td className="py-3 px-4 whitespace-nowrap">
                          {isVerified ? (
                            <div className="flex items-center space-x-1.5 text-emerald-600 dark:text-emerald-400 font-semibold text-[11px]">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              <span>Verified & Active</span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span className="px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 font-mono text-[10px] font-bold">
                                Code: {member.verificationCode || 'Pending'}
                              </span>
                              <button
                                onClick={() => handleManualVerify(member.id)}
                                title="Instantly verify user email so they can sign in"
                                className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 text-[10px] font-bold hover:bg-emerald-500/20 transition-colors"
                              >
                                Verify Now
                              </button>
                            </div>
                          )}
                        </td>

                        <td className="py-3 px-4 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end space-x-1.5">
                            <button
                              onClick={() => handleRegenerateCredentials(member)}
                              title="Regenerate temporary password & view credentials"
                              className="p-1.5 rounded-lg text-surface-500 hover:text-brand-600 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                            >
                              <Key className="w-4 h-4" />
                            </button>

                            <button
                              onClick={() => {
                                setSetPasswordUser(member);
                                setNewCustomPassword('');
                                setPasswordActionError(null);
                                setPasswordActionSuccess(null);
                              }}
                              title="Set specific password directly"
                              className="p-1.5 rounded-lg text-surface-500 hover:text-purple-600 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                            >
                              <Lock className="w-4 h-4" />
                            </button>

                            {member.id !== user?.id && (
                              <button
                                onClick={() => setMemberToDelete(member)}
                                title="Remove / Revoke user access"
                                className="p-1.5 rounded-lg text-surface-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {filteredMembers.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-surface-400">
                        No team members matching your search filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ================= TAB 2: AUDIT TRAIL ================= */}
      {activeTab === 'audit' && (
        <div className="space-y-6">
          {/* Filter Bar */}
          <div className="p-3.5 sm:p-4 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800 shadow-card flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
              <select
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value)}
                className="text-xs px-3 py-1.5 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-700 dark:text-surface-300 focus:outline-none"
              >
                <option value="">All Security Actions</option>
                <option value="USER_LOGIN">USER_LOGIN</option>
                <option value="USER_CREATE">USER_CREATE</option>
                <option value="USER_UPDATE">USER_UPDATE</option>
                <option value="USER_PASSWORD_CHANGE">USER_PASSWORD_CHANGE</option>
                <option value="USER_CREDENTIALS_RESEND">USER_CREDENTIALS_RESEND</option>
                <option value="USER_ADMIN_VERIFY">USER_ADMIN_VERIFY</option>
                <option value="PROJECT_CREATE">PROJECT_CREATE</option>
                <option value="TASK_CREATE">TASK_CREATE</option>
                <option value="STATUS_CHANGED">STATUS_CHANGED</option>
                <option value="FILE_UPLOAD">FILE_UPLOAD</option>
              </select>

              <select
                value={filterEntity}
                onChange={(e) => setFilterEntity(e.target.value)}
                className="text-xs px-3 py-1.5 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-700 dark:text-surface-300 focus:outline-none"
              >
                <option value="">All Entities</option>
                <option value="user">Users</option>
                <option value="project">Projects</option>
                <option value="task">Tasks</option>
                <option value="file">Files</option>
              </select>
            </div>

            <button
              onClick={fetchLogs}
              disabled={isLoadingLogs}
              className="px-3 py-1.5 rounded-xl border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 text-surface-700 dark:text-surface-200 text-xs font-semibold flex items-center space-x-1.5 hover:bg-surface-50 transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isLoadingLogs ? 'animate-spin' : ''}`} />
              <span>Refresh Log</span>
            </button>
          </div>

          {/* Logs Table */}
          <div className="bg-white dark:bg-surface-900 rounded-2xl shadow-card border border-surface-200 dark:border-surface-800 overflow-hidden">
            <div
              onWheel={(e) => {
                if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || e.deltaY === 0) return;
                const el = e.currentTarget;
                const canScrollHorizontally =
                  (e.deltaY > 0 && el.scrollLeft + el.clientWidth < el.scrollWidth - 2) ||
                  (e.deltaY < 0 && el.scrollLeft > 1);
                if (e.shiftKey && canScrollHorizontally) {
                  e.preventDefault();
                  el.scrollLeft += e.deltaY;
                }
              }}
              className="overflow-x-auto touch-scroll"
            >
              <table className="w-full min-w-[760px] text-left text-xs">
                <thead className="bg-surface-50 dark:bg-surface-800/60 border-b border-surface-200 dark:border-surface-800 uppercase tracking-wider text-[11px] font-semibold text-surface-500">
                  <tr>
                    <th className="py-3 px-4">Timestamp</th>
                    <th className="py-3 px-4">Actor</th>
                    <th className="py-3 px-4">Action</th>
                    <th className="py-3 px-4">Target Entity</th>
                    <th className="py-3 px-4">Metadata</th>
                    <th className="py-3 px-4">IP Address</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-100 dark:divide-surface-800/60">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-surface-50 dark:hover:bg-surface-800/40 transition-colors">
                      <td className="py-3 px-4 font-mono text-surface-500 whitespace-nowrap">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 font-medium text-surface-900 dark:text-surface-100 whitespace-nowrap">
                        {log.userName || log.userEmail || log.userId}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className="font-mono px-2 py-0.5 rounded bg-surface-100 dark:bg-surface-800 text-brand-600 dark:text-brand-400 font-semibold text-[11px]">
                          {log.action}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-surface-600 dark:text-surface-400 whitespace-nowrap" title={`${log.entityType} (${log.entityId})`}>
                        {log.targetName || log.targetEmail ? (
                          <div className="leading-tight">
                            <div className="font-medium text-surface-900 dark:text-surface-100">
                              {log.targetName || log.targetEmail}
                            </div>
                            {log.targetName && log.targetEmail && (
                              <div className="text-[11px] text-surface-500">{log.targetEmail}</div>
                            )}
                            <div className="text-[10px] uppercase tracking-wider text-surface-400">{log.entityType}</div>
                          </div>
                        ) : (
                          <span className="capitalize">
                            {log.entityType} ({log.entityId})
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-surface-500 max-w-xs truncate font-mono text-[11px]">
                        {log.metadata ? JSON.stringify(log.metadata) : '—'}
                      </td>
                      <td className="py-3 px-4 text-surface-400 font-mono text-[11px] whitespace-nowrap">
                        {log.ipAddress || '127.0.0.1'}
                      </td>
                    </tr>
                  ))}

                  {logs.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-surface-400">
                        No audit records matching the specified filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ================= TAB 3: ROLES & PERMISSIONS (RBAC) ================= */}
      {activeTab === 'rbac' && <RolesPermissionsView />}

      {/* ================= MODAL: ADD USER & GENERATE CREDENTIALS ================= */}
      <Modal
        isOpen={isAddUserOpen}
        onClose={() => setIsAddUserOpen(false)}
        title="Create New User & Generate Credentials"
      >
        <form onSubmit={handleCreateUserSubmit} className="space-y-4">
          {newUserError && (
            <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-400 text-xs flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{newUserError}</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-surface-700 dark:text-surface-300 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              required
              placeholder="e.g. Wania Kamran"
              value={newFullName}
              onChange={(e) => setNewFullName(e.target.value)}
              className="w-full text-xs px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-surface-700 dark:text-surface-300 mb-1">
              Email Address *
            </label>
            <input
              type="email"
              required
              placeholder="e.g. wania@ihbdigital.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="w-full text-xs px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-surface-700 dark:text-surface-300 mb-1">
                Role *
              </label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as UserRole)}
                className="w-full text-xs px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <option value="member">Member</option>
                <option value="team_lead">Team Lead</option>
                <option value="admin">Admin</option>
                <option value="sqa">SQA</option>
                <option value="guest">Guest</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-surface-700 dark:text-surface-300 mb-1">
                Department / Space
              </label>
              <select
                value={newDepartmentId}
                onChange={(e) => setNewDepartmentId(e.target.value)}
                className="w-full text-xs px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <option value="">General Workspace</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-surface-700 dark:text-surface-300 mb-1">
              Designation / Title
            </label>
            <input
              type="text"
              placeholder="e.g. Frontend Specialist"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="w-full text-xs px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>

          {/* Generated Password Preview */}
          <div className="p-3.5 rounded-xl bg-surface-50 dark:bg-surface-800/70 border border-surface-200 dark:border-surface-700">
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] font-bold text-surface-700 dark:text-surface-300">
                Initial Password
              </label>
              <button
                type="button"
                onClick={() => setNewPassword(generateSecurePassword())}
                className="text-[11px] text-brand-600 dark:text-brand-400 font-semibold hover:underline flex items-center space-x-1"
              >
                <RefreshCw className="w-3 h-3" />
                <span>Regenerate</span>
              </button>
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full text-xs font-mono px-3 py-2 rounded-lg bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 pr-10 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
              >
                {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-2 pt-2">
            <button
              type="button"
              onClick={() => setIsAddUserOpen(false)}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-surface-600 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmittingNewUser}
              className="px-4 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white transition-colors flex items-center space-x-1.5 shadow-sm"
            >
              {isSubmittingNewUser && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
              <span>Create User & Generate Credentials</span>
            </button>
          </div>
        </form>
      </Modal>

      {/* ================= MODAL: CREDENTIALS RESULT DIALOG ================= */}
      <Modal
        isOpen={Boolean(credentialsResult)}
        onClose={() => setCredentialsResult(null)}
        title="User Credentials Generated"
      >
        {credentialsResult && (
          <div className="space-y-4">
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs flex items-center space-x-2">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>{credentialsResult.message || 'Credentials generated successfully.'}</span>
            </div>

            {/* Credentials Card */}
            <div className="p-4 rounded-2xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 space-y-3 font-mono text-xs">
              <div>
                <span className="text-[10px] text-surface-400 uppercase tracking-wider block font-sans">User Email</span>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="text-surface-900 dark:text-surface-100 font-bold select-all">
                    {credentialsResult.user?.email}
                  </span>
                  <button
                    onClick={() => copyToClipboard(credentialsResult.user?.email || '', 'email')}
                    className="p-1 rounded text-surface-400 hover:text-surface-700"
                  >
                    {copiedKey === 'email' ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>

              {credentialsResult.generatedPassword && (
                <div>
                  <span className="text-[10px] text-surface-400 uppercase tracking-wider block font-sans">Temporary Password</span>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-brand-600 dark:text-brand-400 font-bold select-all">
                      {credentialsResult.generatedPassword}
                    </span>
                    <button
                      onClick={() => copyToClipboard(credentialsResult.generatedPassword || '', 'pass')}
                      className="p-1 rounded text-surface-400 hover:text-surface-700"
                    >
                      {copiedKey === 'pass' ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              )}

              {credentialsResult.verificationCode && (
                <div>
                  <span className="text-[10px] text-surface-400 uppercase tracking-wider block font-sans">6-Digit Verification Code</span>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="text-amber-600 dark:text-amber-400 font-bold select-all">
                      {credentialsResult.verificationCode}
                    </span>
                    <button
                      onClick={() => copyToClipboard(credentialsResult.verificationCode || '', 'code')}
                      className="p-1 rounded text-surface-400 hover:text-surface-700"
                    >
                      {copiedKey === 'code' ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Quick 1-Click Copy All Payload */}
            <button
              onClick={() => {
                const payload = `IHB Pulse Credentials:\nEmail: ${credentialsResult.user?.email}\nPassword: ${credentialsResult.generatedPassword || '—'}\nVerification Code: ${credentialsResult.verificationCode || '—'}\nURL: ${window.location.origin}`;
                copyToClipboard(payload, 'all');
              }}
              className="w-full py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-bold text-xs flex items-center justify-center space-x-2 transition-colors shadow-sm"
            >
              {copiedKey === 'all' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              <span>{copiedKey === 'all' ? 'Copied Full Credentials to Clipboard!' : 'Copy All Credentials for Employee'}</span>
            </button>
          </div>
        )}
      </Modal>

      {/* ================= MODAL: SET SPECIFIC PASSWORD ================= */}
      <Modal
        isOpen={Boolean(setPasswordUser)}
        onClose={() => setSetPasswordUser(null)}
        title={`Set Password for ${setPasswordUser?.fullName || 'User'}`}
      >
        {setPasswordUser && (
          <form onSubmit={handleSetPasswordSubmit} className="space-y-4">
            <p className="text-xs text-surface-500">
              Directly override and set a new password for <strong>{setPasswordUser.email}</strong>. The user will be automatically marked as verified and can log in immediately.
            </p>

            {passwordActionError && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-400 text-xs flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{passwordActionError}</span>
              </div>
            )}

            {passwordActionSuccess && (
              <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs flex items-center space-x-2">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span>{passwordActionSuccess}</span>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-surface-700 dark:text-surface-300 mb-1">
                New Password (minimum 6 characters) *
              </label>
              <input
                type="text"
                required
                placeholder="Enter new password"
                value={newCustomPassword}
                onChange={(e) => setNewCustomPassword(e.target.value)}
                className="w-full text-xs font-mono px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div className="flex items-center justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={() => setSetPasswordUser(null)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-surface-600 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmittingPassword}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white transition-colors flex items-center space-x-1.5 shadow-sm"
              >
                {isSubmittingPassword && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                <span>Save New Password</span>
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* ================= MODAL: MULTI-USER INVITE ================= */}
      <InviteModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        onSuccess={() => fetchMembers()}
      />

      {/* ================= MODAL: DELETE CONFIRMATION ================= */}
      <Modal
        isOpen={Boolean(memberToDelete)}
        onClose={() => setMemberToDelete(null)}
        title="Revoke User Access"
      >
        {memberToDelete && (
          <div className="space-y-4">
            <p className="text-xs text-surface-600 dark:text-surface-300 leading-relaxed">
              Are you sure you want to deactivate and remove access for <strong>{memberToDelete.fullName}</strong> ({memberToDelete.email})?
            </p>
            <div className="flex items-center justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={() => setMemberToDelete(null)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-surface-600 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={() => handleDeleteUser(memberToDelete.id)}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-rose-600 hover:bg-rose-500 text-white transition-colors flex items-center space-x-1.5 shadow-sm"
              >
                {isDeleting && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                <span>Confirm Revoke Access</span>
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
